import DbApi from "common-library/types/DbApi"
import angular, { IScope } from "angular"
import _ from "lodash"
import IoT from "common-library/types/IoT"
import { Nullable } from "common-library/utils/types"

type VcaConfigFormDetectionPoint = {
  name: "Center" | "Bottom"
  value: IoT.Tracker.DetectionPoint
}

const ENGINE_TR: { [k in IoT.TrackingEngine.Id]: string } = {
  dl_people_tracker: "DLPT",
  dl_skeleton_tracker: "DLST",
  dl_thermal_tracker: "DLTT",
  dl_fisheye_tracker: "DLFT",
  dl_object_tracker: "DLOT",
  object_tracker: "OT",
  unknown_tracker: "Unknown Tracker",
}

type VcaConfigFormTrackingEngine = {
  name: string
  value: IoT.TrackingEngine.Id | null // `null`: Sensors setting
}

type VcaConfigForm = Nullable<{
  detectionPoint?: VcaConfigFormDetectionPoint
  trackingEngine?: VcaConfigFormTrackingEngine
}>

export type VcaConfigFormData = Nullable<Omit<DbApi.VcaConfig.Data, "_cameraId" | "_id">>

interface ControllerScope extends IScope {
  data?: VcaConfigFormData
  deviceVca?: IoT.Device.Description["properties"]["vca"]
  licenses?: IoT.License.Data[]
  noLicenseValidation?: boolean
  availTrackingEngines: IoT.TrackingEngine.Id[]
  isNew: boolean
  onChange: (data: VcaConfigFormData) => void

  form: VcaConfigForm
  detectionPoints: VcaConfigFormDetectionPoint[]
  trackingEngines: VcaConfigFormTrackingEngine[]
  sensorsAsDefault?: boolean
  handleChange: () => void
}

angular.module("uCountitUiApp").directive("vcaConfigForm", () => ({
  restrict: "E",
  templateUrl: "components/vcaconfig/VcaConfigForm.html",
  scope: {
    data: "=",
    onChange: "=",
    licenses: "=",
    deviceVca: "=",
    noLicenseValidation: "=",
    availTrackingEngines: "=",
    isNew: "=",
    sensorsAsDefault: "=?",
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controller: ($scope: ControllerScope, Locale: any) => {
    let didSetDefault = false

    const resetDefaultAndUpdateForm = () => {
      didSetDefault = false
      if ($scope.data) {
        updateForm($scope.data)
      }
    }

    // Feature 변경으로 사용 가능한 TrackEngine이 바뀌면 새 센서의 기본값을 재설정
    $scope.$watchCollection("availTrackingEngines", () => {
      if ($scope.isNew) {
        resetDefaultAndUpdateForm()
      } else if ($scope.data) {
        updateForm($scope.data)
      }
    })

    // 기존 센서 → 새 센서 전환 시 기본값 재설정
    $scope.$watch("isNew", (newVal, oldVal) => {
      if (newVal === true && oldVal !== true) {
        resetDefaultAndUpdateForm()
      }
    })

    // data, deviceVca, licenses, noLicenseValidation 변경 시 폼 업데이트
    $scope.$watchGroup(["data", "deviceVca", "licenses", "noLicenseValidation"], () => {
      if ($scope.data) {
        updateForm($scope.data)
      }
    })

    $scope.$watch<VcaConfigForm>(
      "form",
      (newVals: VcaConfigForm, oldVals: VcaConfigForm) => {
        if (!angular.equals(newVals, oldVals)) {
          $scope.handleChange()
        }
      },
      true
    )

    function updateForm(data: VcaConfigFormData) {
      if (!$scope.form) {
        $scope.form = {}
      }

      updateTrackingEngineOptions()

      if ($scope.isNew) {
        if (!didSetDefault) {
          $scope.form.trackingEngine = $scope.trackingEngines[0]
          didSetDefault = true
        } else {
          // 이미 기본값이 설정된 새 센서: 현재 폼 값을 유지하면서 옵션 매핑만 갱신
          $scope.form.trackingEngine = getTrackingEngineOption($scope.form.trackingEngine?.value)
        }
        updateDpOptions()
        return
      }

      $scope.form.trackingEngine = getTrackingEngineOption(data.trackingEngine)
      updateDpOptions()
      $scope.form.detectionPoint = getDetectionPointOption(data.detectionPoint)
    }

    function getTrackingEngineOption(
      trackingEngine?: IoT.TrackingEngine.Id | null
    ): VcaConfigFormTrackingEngine {
      return (
        $scope.trackingEngines.find((te) => te.value === trackingEngine) ??
        $scope.trackingEngines[0]
      )
    }

    function getDetectionPointOption(
      dpValue?: IoT.Tracker.DetectionPoint | null
    ): VcaConfigFormDetectionPoint {
      return $scope.detectionPoints.find((dp) => dp.value == dpValue) ?? $scope.detectionPoints[0]
    }

    function updateTrackingEngineOptions() {
      let trackingEngines: IoT.TrackingEngine.Id[] = []
      if ($scope.noLicenseValidation) {
        trackingEngines = $scope.deviceVca?.trackingEngines.map((te) => te.id) ?? []
      } else {
        trackingEngines = _.intersection(
          _.uniq($scope.licenses?.map((l) => l.trackingEngines).flat() ?? []),
          $scope.deviceVca?.trackingEngines.map((te) => te.id) ?? ([] as IoT.TrackingEngine.Id[])
        )
      }

      $scope.trackingEngines = trackingEngines.map((v) => ({
        name: ENGINE_TR[v],
        value: v,
      })) as VcaConfigFormTrackingEngine[]

      if ($scope.availTrackingEngines && $scope.availTrackingEngines.length > 0) {
        $scope.trackingEngines = $scope.trackingEngines.filter(
          (te) => te.value && $scope.availTrackingEngines.includes(te.value)
        )
      }

      const sensorsSetting = {
        name: Locale.string("t18n use sensor's setting"),
        value: null,
      }

      if ($scope.sensorsAsDefault) {
        $scope.trackingEngines.unshift(sensorsSetting)
      } else {
        $scope.trackingEngines.push(sensorsSetting)
      }
    }

    function updateDpOptions() {
      const teId = $scope.form?.trackingEngine?.value
      let dps: IoT.Tracker.DetectionPoint[] | undefined = undefined
      if (teId) {
        dps = $scope.deviceVca?.trackingEngines.find((te) => te.id == teId)?.dps
      }
      if (teId && dps) {
        $scope.detectionPoints = dps.map((v) => ({
          name: Locale.string(v.charAt(0).toUpperCase() + v.slice(1)),
          value: v,
        })) as VcaConfigFormDetectionPoint[]
      } else {
        $scope.detectionPoints = []
      }
    }

    function form2data(form: Partial<VcaConfigForm>): VcaConfigFormData {
      return {
        detectionPoint: form.detectionPoint?.value ?? null,
        trackingEngine: form.trackingEngine?.value ?? null,
      }
    }

    $scope.handleChange = () => {
      $scope.onChange(form2data($scope.form))
    }
  },
}))
